import {
    appendUniqueAssignmentResourceEntries,
    focusFirstAssignmentResourceEntry,
    initializeAssignmentResourceRows,
    replaceAssignmentResourceList,
    updateAssignmentResourceLiveRegion
} from './assignment_resource_list';

document.addEventListener('turbolinks:load', function() {
    const assignment_library_content = document.querySelector("#assignment_resource");
    if (assignment_library_content == null) {
        return
    }

    const folderListSelector = '[data-assignment-resource-folder-list]';
    const folderPaginationSelector = '[data-assignment-resource-folder-pagination]';
    const folderLoadMoreSelector = '[data-assignment-resource-folder-load-more]';
    const folderRequests = new Map();

    const folderListForCollapse = (collapseElement) => {
        return collapseElement.querySelector(folderListSelector);
    };

    const setFolderLoadingState = (list, loading) => {
        const pagination = list.querySelector(folderPaginationSelector);
        const button = pagination?.querySelector(folderLoadMoreSelector);

        list.dataset.folderLoading = loading.toString();
        list.setAttribute('aria-busy', loading.toString());
        if (button) {
            button.disabled = loading;
            button.textContent = loading
                ? 'Loading assignments…'
                : (list.dataset.folderLoaded === 'true' ? 'Load more assignments' : 'Load assignments');
        }
    };

    const announceFolderLoad = (list, message) => {
        const status = list.querySelector('[data-assignment-resource-folder-status]');
        updateAssignmentResourceLiveRegion(status, message);
    };

    const initializeLoadedFolderRows = (list, entries) => {
        initializeAssignmentResourceRows(entries, {
            initializeSorting: list.classList.contains('assignment-sortable')
        });
    };

    const loadFolderPage = (list, options = {}) => {
        const resourceGroupId = list.dataset.resourceGroupId;
        const nextPageUrl = list.dataset.nextPageUrl;
        if (!resourceGroupId || !nextPageUrl) return null;
        if (list.dataset.folderLoading === 'true') return folderRequests.get(resourceGroupId) || null;

        let loadFailed = false;
        setFolderLoadingState(list, true);
        const request = $.ajax({
            url: nextPageUrl,
            type: 'GET',
            dataType: 'json'
        }).done((response) => {
            if (!list.isConnected) return;

            const pagination = list.querySelector(folderPaginationSelector);
            if (!pagination) return;

            const appendedEntries = appendUniqueAssignmentResourceEntries(list, pagination, response.items_html);
            const appendedCount = appendedEntries.length;
            const fullyLoaded = !response.has_more;
            list.dataset.folderLoaded = 'true';
            list.dataset.folderFullyLoaded = fullyLoaded.toString();
            list.dataset.nextPageUrl = response.next_page_url || '';
            list.dataset.totalCount = response.total_count?.toString() || list.dataset.totalCount || '0';
            announceFolderLoad(
                list,
                `${appendedCount} ${appendedCount === 1 ? 'assignment' : 'assignments'} loaded.`
            );

            const button = list.querySelector(folderLoadMoreSelector);
            if (button) button.hidden = fullyLoaded;
            initializeLoadedFolderRows(list, appendedEntries);
            if (options.moveFocus) focusFirstAssignmentResourceEntry(appendedEntries);
        }).fail((_xhr, status) => {
            if (status === 'abort' || !list.isConnected) return;

            loadFailed = true;
            announceFolderLoad(list, 'Assignments could not be loaded. Use the retry button to try again.');
        }).always(() => {
            folderRequests.delete(resourceGroupId);
            if (list.isConnected) {
                setFolderLoadingState(list, false);
                const button = list.querySelector(folderLoadMoreSelector);
                if (loadFailed && button) button.textContent = 'Retry loading assignments';
            }
        });

        folderRequests.set(resourceGroupId, request);
        return request;
    };

    const folderListForResourceGroupId = (resourceGroupId) => {
        return document.getElementById(`resource-group-${resourceGroupId}-assignments`);
    };

    const requestCompletion = (request) => {
        if (!request) return Promise.resolve(false);

        return new Promise((resolve) => {
            request.done(() => resolve(true));
            request.fail(() => resolve(false));
        });
    };

    window.hasMoreAssignmentResourceFolderPages = (resourceGroupId) => {
        const list = folderListForResourceGroupId(resourceGroupId);
        return Boolean(list?.dataset.nextPageUrl);
    };

    window.loadNextAssignmentResourceFolderPage = (resourceGroupId, options = {}) => {
        const list = folderListForResourceGroupId(resourceGroupId);
        if (!list) return Promise.resolve(false);

        return requestCompletion(loadFolderPage(list, options));
    };

    window.initializeAssignmentResourceFolders = () => {
        document.querySelectorAll(`#assignment_resource .collapse.show ${folderListSelector}`).forEach((list) => {
            if (list.dataset.folderLoaded !== 'true') loadFolderPage(list);
        });
    };

    const updateResourceGroupFolderIcon = (resourceGroupId, expanded) => {
        const icon = document.querySelector(
            `[data-resource-group-id="${resourceGroupId}"] .assignment-resource-folder-icon`
        );
        if (!icon) return;

        const source = expanded ? icon.dataset.folderOpenSrc : icon.dataset.folderClosedSrc;
        if (source) {
            icon.setAttribute('src', source);
        }
    };

    const resourceGroupIdFromCollapse = (collapseElement) => {
        return collapseElement.id.replace('resourceGroupCollapse-', '');
    };

    $(document)
        .off('shown.bs.collapse.assignmentResourceFolderIcons', '#assignment_resource .collapse')
        .on('shown.bs.collapse.assignmentResourceFolderIcons', '#assignment_resource .collapse', function() {
            updateResourceGroupFolderIcon(resourceGroupIdFromCollapse(this), true);
            const list = folderListForCollapse(this);
            if (list && list.dataset.folderLoaded !== 'true') loadFolderPage(list);
        })
        .off('hidden.bs.collapse.assignmentResourceFolderIcons', '#assignment_resource .collapse')
        .on('hidden.bs.collapse.assignmentResourceFolderIcons', '#assignment_resource .collapse', function() {
            updateResourceGroupFolderIcon(resourceGroupIdFromCollapse(this), false);
        });

    $(document)
        .off('click.assignmentResourceFolderPagination', folderLoadMoreSelector)
        .on('click.assignmentResourceFolderPagination', folderLoadMoreSelector, function() {
            const list = this.closest(folderListSelector);
            if (list) loadFolderPage(list, { moveFocus: true });
        });

    document.addEventListener('turbolinks:before-cache', function abortFolderRequests() {
        folderRequests.forEach(request => request.abort());
        folderRequests.clear();
    }, { once: true });

    window.initializeAssignmentResourceFolders();

    window.createFolder = function() {
        $.ajax({
            url: '/assignment_resource_groups',
            type: 'POST',
            data: {
                name: 'New Folder'
            },
            success: function(response) {
                if (!response || !response.success || !response.group_id) {
                    alert('Failed to create folder. Please try again.');
                    return;
                }

                refreshAssignmentResourcesList(function(refreshed) {
                    if (!refreshed) {
                        return;
                    }

                    const selector = `[data-resource-group-id="${response.group_id}"] .assignment_resource__main_panel__form__resources__card__primary-information-description-name`;
                    const maxAttempts = 5;
                    function startEditingWhenReady(attempt = 0) {
                        if (document.querySelector(selector)) {
                            makeResourceGroupNameEditable(response.group_id, response.group_name || 'New Folder');
                            return;
                        }

                        if (attempt >= maxAttempts) {
                            console.error('Could not find nameButton element');
                            return;
                        }

                        setTimeout(() => startEditingWhenReady(attempt + 1), 100);
                    }

                    startEditingWhenReady();
                });
            },
            error: function(xhr, status, error) {
                alert('Failed to create folder. Please try again.');
            }
        });
    }

    // Shared helper to refresh just the assignment resources list.
    window.refreshAssignmentResourcesList = function(onSuccess) {
        if (editingResourceGroupId !== null) {
            if (typeof onSuccess === 'function') {
                onSuccess(false);
            }
            return;
        }
        // Get current URL parameters to maintain state
        const urlParams = new URLSearchParams(window.location.search);
        const premade = urlParams.get('premade') || 'false';
        const defaultData = { premade: premade, format: 'json' }

        $.ajax({
            url: '/assignment_resources',
            type: 'GET',
            dataType: 'json',
            data: window.filterData || defaultData,
            success: function(response) {
                // Update the assignment resources list with the new HTML
                replaceAssignmentResourceList(response.assignment_resources_html, {
                    totalCount: response.total_count
                });

                if (typeof onSuccess === 'function') {
                    onSuccess(true);
                }
            },
            error: function(xhr, status, error) {
                console.error('Error refreshing assignment resources:', error);
                if (typeof onSuccess === 'function') {
                    onSuccess(false);
                }
            }
        });
    }

    if (typeof editingResourceGroupId === 'undefined') {
        // Track which resource group is being edited
        var editingResourceGroupId = null;
    }

    const closeOpenDropdowns = () => {
        $('.dropdown-menu').removeClass('show');
        $('.dropdown').removeClass('show');
    }


    window.makeResourceGroupNameEditable = function(resourceGroupId, currentName) {
        // Prevent multiple edits at once
        if (editingResourceGroupId !== null) {
            return;
        }

        closeOpenDropdowns();

        const nameButton = document.querySelector(`[data-resource-group-id="${resourceGroupId}"] .assignment_resource__main_panel__form__resources__card__primary-information-description-name`);

        if (nameButton) {
            // Mark this resource group as being edited
            editingResourceGroupId = resourceGroupId;

            // Store the original button for later restoration
            const originalButton = nameButton.cloneNode(true);

            // Create an input field
            const input = document.createElement('input');
            input.type = 'text';
            input.value = currentName;
            input.className = 'form-control resource-group-name-input';
            input.style.display = 'inline-block';
            input.style.width = 'auto';
            input.style.minWidth = '200px';
            input.setAttribute('data-editing-resource-group', resourceGroupId);
            input.setAttribute('aria-label', `Folder name for ${currentName}`);

            // Replace button with input
            const parent = nameButton.parentNode;

            try {
                parent.replaceChild(input, nameButton);

                // Focus and select text after a small delay
                setTimeout(() => {
                    if (input.parentNode) {
                        input.focus();
                        input.select();
                    }
                }, 200);
            } catch (error) {
                editingResourceGroupId = null;
                return;
            }

            function saveResourceGroupName() {
                const newName = input.value.trim();
                editingResourceGroupId = null;

                if (newName && newName !== currentName) {
                    $.ajax({
                        url: `/assignment_resource_groups/${resourceGroupId}/rename`,
                        type: 'PATCH',
                        data: {
                            resource_group_id: resourceGroupId,
                            new_name: newName
                        },
                        success: function(response) {
                            if (response.success) {
                                refreshAssignmentResourcesList();
                            } else {
                                alert('Failed to rename folder: ' + response.message);
                                revertToButton();
                            }
                        },
                        error: function(xhr, status, error) {
                            console.error('Error renaming folder:', error);
                            alert('Failed to rename folder. Please try again.');
                            revertToButton();
                        }
                    });
                } else {
                    revertToButton();
                }
            }

            function revertToButton() {
                editingResourceGroupId = null; // Clear the editing flag

                if (input.parentNode) {
                    try {
                        input.parentNode.replaceChild(originalButton, input);
                    } catch (error) {
                        console.error('Error reverting to button:', error);
                    }
                }
            }

            // Event listeners
            input.addEventListener('keydown', function(e) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    saveResourceGroupName();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    revertToButton();
                }
            });

            input.addEventListener('blur', saveResourceGroupName);

        } else {
            console.warn(`Could not find nameButton element for resource group ${resourceGroupId}. Element may not be rendered yet. Retrying...`);
            // Retry after a short delay
            setTimeout(() => {
                makeResourceGroupNameEditable(resourceGroupId, currentName);
            }, 200);
        }
    }

    // Handle delete folder modal
    $(document).off("show.bs.modal.assignmentResources", "#delete_resource_group_modal")
        .on("show.bs.modal.assignmentResources", "#delete_resource_group_modal", function(event) {
        var button = $(event.relatedTarget);
        var deleteUrl = button.data("url");

        // Store the URL in the modal for later use
        $(this).data("delete-url", deleteUrl);

        // Close any open dropdowns
        closeOpenDropdowns();
    });

    $(document).off("show.bs.modal.assignmentResources", "#delete_resource_group_grades_warning_modal")
        .on("show.bs.modal.assignmentResources", "#delete_resource_group_grades_warning_modal", function(event) {
            const button = $(event.relatedTarget);
            const deleteUrl = button && button.length ? button.data("url") : $(this).data("delete-url");
            const lmsName = button && button.length ? button.data("lms-name") : $(this).data("lms-name");

            $(this).data("delete-url", deleteUrl);
            $(this).data("lms-name", lmsName);
            $("#delete_folder_grades_warning_lms_name").text(lmsName || "LMS");
            closeOpenDropdowns();
    });

    // Handle the AJAX delete when confirm button is clicked
    $(document).off("click.assignmentResources", "#confirmDeleteFolder")
        .on("click.assignmentResources", "#confirmDeleteFolder", function(e) {
            e.preventDefault();

        let modal = $('#delete_resource_group_modal');
        let deleteUrl = modal.data('delete-url');
        let confirmButton = $(this);

        // Disable the button to prevent double-clicks
        confirmButton.prop('disabled', true).text('Deleting...');

        $.ajax({
            url: deleteUrl,
            type: 'DELETE',
            dataType: 'json',
            success: function(response) {
                if (response.success) {
                    // Close the modal
                    modal.modal('hide');

                    // Refresh the assignment resources list
                    refreshAssignmentResourcesList();
                } else {
                    alert('Failed to delete folder: ' + response.message);
                }
            },
            error: function(xhr, status, error) {
                console.error('Error deleting folder:', error);
                alert('Failed to delete folder. Please try again.');
            },
            complete: function() {
                // Re-enable the button
                confirmButton.prop('disabled', false).text('Delete Folder');
            }
        });
    });

    // Reset modal when it's hidden
    $(document).off("hidden.bs.modal.assignmentResources", "#delete_resource_group_modal")
        .on("hidden.bs.modal.assignmentResources", "#delete_resource_group_modal", function() {
            $('#folderNameToDelete').text('');
        $(this).removeData('delete-url');
    });

    // Handle delete assignment grade warning modal
    $(document).off("show.bs.modal.assignmentResources", "#delete_assignment_grades_warning_modal")
        .on("show.bs.modal.assignmentResources", "#delete_assignment_grades_warning_modal", function(event) {
            const button = $(event.relatedTarget);
            const deleteUrl = button.data("url");
            const assignmentName = button.data("assignment-name");
            const lmsName = button.data("lms-name");

            // Carry context into the warning modal copy and follow-up confirmation step.
            $(this).data("delete-url", deleteUrl);
            $("#delete_assignment_grades_warning_assignment_name").text(assignmentName);
            $("#delete_assignment_grades_warning_lms_name").text(lmsName);
            closeOpenDropdowns();
        });

    // Proceed from first warning to unrecoverable confirmation modal
    $(document).off("click.assignmentResources", "#proceedDeleteAssignmentFromGradesWarning")
        .on("click.assignmentResources", "#proceedDeleteAssignmentFromGradesWarning", function(event) {
            event.preventDefault();

            const gradesWarningModal = $('#delete_assignment_grades_warning_modal');
            const confirmModal = $('#delete_assignment_confirm_modal');
            const deleteUrl = gradesWarningModal.data('delete-url');

            // Preserve target URL when transitioning between the two modals.
            confirmModal.data('delete-url', deleteUrl);
            gradesWarningModal.one('hidden.bs.modal', function() {
                confirmModal.modal('show');
            });
            gradesWarningModal.modal('hide');
        });

    $(document).off("click.assignmentResources", "#proceedDeleteFolderFromGradesWarning")
        .on("click.assignmentResources", "#proceedDeleteFolderFromGradesWarning", function(event) {
            event.preventDefault();

            const gradesWarningModal = $('#delete_resource_group_grades_warning_modal');
            const confirmModal = $('#delete_resource_group_modal');
            const deleteUrl = gradesWarningModal.data('delete-url');

            // Preserve target URL when transitioning between the two modals.
            confirmModal.data('delete-url', deleteUrl);
            gradesWarningModal.one('hidden.bs.modal', function() {
                confirmModal.modal('show');
            });
            gradesWarningModal.modal('hide');
        });

    // Handle delete assignment unrecoverable confirmation modal
    $(document).off("show.bs.modal.assignmentResources", "#delete_assignment_confirm_modal")
        .on("show.bs.modal.assignmentResources", "#delete_assignment_confirm_modal", function(event) {
            const button = $(event.relatedTarget);
            const deleteUrl = button.data("url") || $(this).data("delete-url");

            $(this).data("delete-url", deleteUrl);
            closeOpenDropdowns();
        });

    // Confirm assignment delete
    $(document).off("click.assignmentResources", "#confirmDeleteAssignment")
        .on("click.assignmentResources", "#confirmDeleteAssignment", function(event) {
            event.preventDefault();

            const modal = $('#delete_assignment_confirm_modal');
            const deleteUrl = modal.data('delete-url');
            const confirmButton = $(this);

            if (!deleteUrl) {
                alert('Could not determine assignment to delete.');
                return;
            }

            confirmButton.prop('disabled', true).text('Deleting...');

            $.ajax({
                url: deleteUrl,
                type: 'DELETE',
                dataType: 'json',
                success: function(response) {
                    if (response.success) {
                        modal.modal('hide');
                        refreshAssignmentResourcesList();
                    } else {
                        alert('Failed to delete assignment: ' + response.message);
                    }
                },
                error: function(xhr, status, error) {
                    console.error('Error deleting assignment:', error);
                    alert('Failed to delete assignment. Please try again.');
                },
                complete: function() {
                    confirmButton.prop('disabled', false).text('Delete');
                }
            });
        });

    $(document).off("hidden.bs.modal.assignmentResources", "#delete_assignment_grades_warning_modal")
        .on("hidden.bs.modal.assignmentResources", "#delete_assignment_grades_warning_modal", function() {
            $(this).removeData('delete-url');
        });

    $(document).off("hidden.bs.modal.assignmentResources", "#delete_resource_group_grades_warning_modal")
        .on("hidden.bs.modal.assignmentResources", "#delete_resource_group_grades_warning_modal", function() {
            $("#delete_folder_grades_warning_lms_name").text('');
            $(this).removeData('delete-url');
            $(this).removeData('lms-name');
        });

    $(document).off("hidden.bs.modal.assignmentResources", "#delete_assignment_confirm_modal")
        .on("hidden.bs.modal.assignmentResources", "#delete_assignment_confirm_modal", function() {
            $(this).removeData('delete-url');
        });

    $(document).off("click.unlinkAccount", "#confirmUnlinkAccount")
        .on("click.unlinkAccount", "#confirmUnlinkAccount", function(event) {
            event.preventDefault();

            $(this).prop('disabled', true).text('Unpairing...');

            $.ajax({
                url: '/lms_user_unlink',
                type: 'DELETE',
                success: function(response) {
                    // The server will handle the redirect
                },
                error: function(xhr, status, error) {
                    console.error('Error unlinking account:', error);
                    alert('Failed to unlink account. Please try again.');
                }
            });
        })

    $('#alib_settings_modal').on('shown.bs.modal', function(event) {
        event.preventDefault();
        // Small delay to let Bootstrap finish its setup
            setTimeout(function() {
                // We then focus the Unpair Account button as we don't want to focus the close X which will be focused by default
                $('#proceedToUnlinkAccount').focus();
            }, 500);
    });

    $('#confirm_unlink_modal').on('shown.bs.modal', function() {
        setTimeout(function() {
            $('#confirmUnlinkAccount').focus();
        }, 100);
    });


    window.toggleResourceGroupState = function(resourceGroupId) {
        $.ajax({
            url: `/assignment_resource_groups/${resourceGroupId}/toggle`,
            type: 'PATCH',
            data: {
                resource_group_id: resourceGroupId
            },
            success: function(response) {
                if (response.success) {
                    const button = document.querySelector(`[data-resource-group-id="${resourceGroupId}"] button`);
                    if (button) {
                        button.setAttribute('aria-expanded', response.opened ? 'true' : 'false');
                    }
                    updateResourceGroupFolderIcon(resourceGroupId, response.opened);
                }
            },
            error: function(xhr, status, error) {
                console.error('Error toggling resource group state:', error);
            }
        });
    };


    // Also prevent other potential DOM updates
    $(document).ajaxComplete(function(event, xhr, settings) {
        // If we're editing and an AJAX call completes, check if our input is still there
        if (editingResourceGroupId !== null) {
            const input = document.querySelector(`input[data-editing-resource-group="${editingResourceGroupId}"]`);
            if (!input) {
                editingResourceGroupId = null;
            }
        }
    });
})
